import { useEffect, useMemo, useState } from 'react';
import {
  assignEmployeeColors,
  extendGanttRange,
  ganttBarFromMinutes,
  ganttTicks,
  type GanttRange,
} from '../boardGantt.js';
import { timeToMinutes } from '../boardCoverage.js';
import { parisHM } from '../domain.js';
import { presenceSectionLabels, type PresenceDay, type PresenceRow } from '../presenceDay.js';

// One business day's punches as an hour-axis Gantt, in the three sections of
// presenceDay: who is open, who is closed, who never punched. Shared by the
// Pointeuse kiosk (« Aujourd'hui ») and the admin « Pointages » day view
// (2026-08-06) — the same chart answering the same question, so it is one
// component rather than two that drift.
//
// Presentational only: it takes a computed PresenceDay and a roster, and owns
// no fetching, no ports and no period state. The only thing it keeps for
// itself is the current minute, because an open bar has to grow.

/** The roster fields the chart needs — a structural subset of Employee/AdminEmployee. */
export interface PresenceGanttEmployee {
  id: string;
  firstName: string;
  lastName: string;
  nickname?: string | null;
}

/** Axis when nothing is drawn yet — a plain working day. */
const FALLBACK_RANGE: GanttRange = { startMin: 6 * 60, endMin: 23 * 60 };

const NOW_TICK_MS = 60_000;

const rowLabel = (e: PresenceGanttEmployee): string =>
  e.nickname || `${e.firstName} ${e.lastName.charAt(0)}.`;

/** The current minute in Europe/Paris — the tablet's own zone may not be French time. */
const nowMinutesParis = (): number => timeToMinutes(parisHM(new Date().toISOString()));

export function PresenceGantt({
  day,
  employees,
  emptyLabel = 'Aucun pointage ce jour.',
}: {
  day: PresenceDay;
  employees: PresenceGanttEmployee[];
  /** Shown when nobody punched and nobody is on the roster for the day. */
  emptyLabel?: string;
}) {
  const isToday = day.position === 'today';
  const [nowMin, setNowMin] = useState(nowMinutesParis);
  useEffect(() => {
    if (!isToday) return; // a past day's chart is static — nothing to tick
    const id = setInterval(() => setNowMin(nowMinutesParis()), NOW_TICK_MS);
    return () => clearInterval(id);
  }, [isToday]);

  const labels = presenceSectionLabels(day.position);
  const byId = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const colors = useMemo(() => assignEmployeeColors(employees.map((e) => e.id)), [employees]);

  // The axis must cover every segment; today it must also reach « now », so an
  // open bar has somewhere to grow.
  const range = useMemo(() => {
    let r = FALLBACK_RANGE;
    for (const row of [...day.openRows, ...day.closedRows]) {
      for (const s of row.segments) r = extendGanttRange(r, s.startMin, s.endMin ?? (isToday ? nowMin : s.startMin));
    }
    return isToday ? extendGanttRange(r, nowMin, nowMin) : r;
  }, [day.openRows, day.closedRows, isToday, nowMin]);

  const ticks = useMemo(() => ganttTicks(range), [range]);
  const gridLines = ticks.map((t) => (
    <span key={t.leftPct} className="pgantt-line" style={{ left: `${t.leftPct}%` }} />
  ));
  const nowPct = ganttBarFromMinutes(range.startMin, nowMin, range).widthPct;

  const axisRow = (
    <div className="pgantt-row pgantt-axis">
      <div className="pgantt-label" />
      <div className="pgantt-track">
        {ticks.map((t) => (
          <span key={t.leftPct} className="pgantt-tick" style={{ left: `${t.leftPct}%` }}>
            {t.label}
          </span>
        ))}
      </div>
    </div>
  );

  const label = (id: string): string => {
    const e = byId.get(id);
    return e ? rowLabel(e) : id;
  };

  const barRows = (rows: PresenceRow[]) =>
    rows.map((row) => (
      <div key={row.employeeId} className="pgantt-row">
        <div className="pgantt-label" title={label(row.employeeId)}>
          <span className="pgantt-dot" style={{ background: colors.get(row.employeeId) }} />
          {label(row.employeeId)}
        </div>
        <div className="pgantt-track">
          {gridLines}
          {isToday && <span className="pgantt-now" style={{ left: `${nowPct}%` }} />}
          {row.segments.map((s, i) => {
            // An open punch has no end. Today it runs to « now »; on a past day
            // the end is genuinely unknown, so the bar runs to the axis end and
            // is hatched — it is a correction to make, not a measurement.
            const end = s.endMin ?? (isToday ? nowMin : range.endMin);
            const bar = ganttBarFromMinutes(s.startMin, end, range);
            return (
              <div
                key={i}
                className={`pgantt-bar${s.endMin === null ? (isToday ? ' is-open' : ' is-unclosed') : ''}`}
                style={{
                  left: `${bar.leftPct}%`,
                  width: `${bar.widthPct}%`,
                  background: colors.get(row.employeeId),
                }}
                title={`${label(row.employeeId)} ${s.label}`}
              >
                <span className="pgantt-bar-time">{s.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    ));

  const chartSection = (kind: string, title: string, rows: PresenceRow[], empty: string, warn = false) => (
    <div className={`pgantt-section is-${kind}${warn && rows.length > 0 ? ' is-warning' : ''}`}>
      <div className="pgantt-section-title">
        {warn && rows.length > 0 && <span className="pgantt-warn">⚠ </span>}
        {title} ({rows.length})
      </div>
      {rows.length === 0 ? (
        <p className="pgantt-empty">{empty}</p>
      ) : (
        <>
          {axisRow}
          {barRows(rows)}
        </>
      )}
    </div>
  );

  if (day.openRows.length + day.closedRows.length + day.noPunchIds.length === 0) {
    return <p className="pgantt-none">{emptyLabel}</p>;
  }

  return (
    <div className="pgantt">
      {chartSection(
        'open',
        labels.open,
        day.openRows,
        isToday ? 'Personne sur place actuellement.' : 'Aucun pointage sans départ.',
        !isToday, // in the past, an open punch is an error to correct
      )}
      {chartSection(
        'closed',
        labels.closed,
        day.closedRows,
        isToday ? 'Personne n’est encore reparti·e.' : 'Personne n’a travaillé ce jour.',
      )}
      <div className="pgantt-section is-none">
        <div className="pgantt-section-title">
          {labels.none} ({day.noPunchIds.length})
        </div>
        {day.noPunchIds.length === 0 ? (
          <p className="pgantt-empty">
            {isToday ? 'Tout le monde a pointé aujourd’hui.' : 'Toute l’équipe a travaillé ce jour.'}
          </p>
        ) : (
          <div className="pgantt-none-list">
            {day.noPunchIds.map((id) => {
              const e = byId.get(id);
              return (
                <span
                  key={id}
                  className="pgantt-none-chip"
                  title={e ? `${e.firstName} ${e.lastName}` : id}
                >
                  <span className="pgantt-dot" style={{ background: colors.get(id) }} />
                  {label(id)}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
